import { useEffect, useMemo, useRef } from 'preact/hooks';
/** Facet group attribute from Fredhopper — groups filters in the drawer UI */
export const FILTER_GROUP_ATTRIBUTE_NAME = 'facet_group';
/** Namespace for filter keys in the URL so they don't clash with theme / other query params. */
export const URL_FILTER_PARAM_PREFIX = 'mrf_';
export function parseFiltersFromURL() {
    if (typeof window === 'undefined')
        return {};
    const params = new URLSearchParams(window.location.search);
    const filters = {};
    for (const [key, value] of params.entries()) {
        if (['page', 'sort', 'q'].includes(key))
            continue;
        if (!key.startsWith(URL_FILTER_PARAM_PREFIX))
            continue;
        const filterKey = key.replace(URL_FILTER_PARAM_PREFIX, '');
        if (!filterKey)
            continue;
        if (!filters[filterKey]) {
            filters[filterKey] = [];
        }
        if (value.includes(',') && /^\d+,\d+$/.test(value)) {
            filters[filterKey].push(value.replace(',', '-'));
        }
        else {
            filters[filterKey].push(value);
        }
    }
    return filters;
}
export function updateURLWithFilters(filters, options) {
    if (typeof window === 'undefined')
        return;
    const params = new URLSearchParams(window.location.search);
    // We need the logic below to retain any parameters (e.g. tracking params like utm_source) that are not related to our preact parameters.
    // Materialise the keys first — deleting inside forEach silently skips later entries because URLSearchParams shifts on delete.
    const keysToDelete = Array.from(params.keys()).filter((key) => key === 'q' ||
        key === 'sort' ||
        key === 'page' ||
        key.startsWith(URL_FILTER_PARAM_PREFIX));
    keysToDelete.forEach((key) => params.delete(key));
    Object.entries(filters).forEach(([filterId, values]) => {
        const paramKey = `${URL_FILTER_PARAM_PREFIX}${filterId}`;
        values.forEach((value) => {
            let urlValue = value;
            if (value.includes('-') && /^\d+-\d+$/.test(value)) {
                urlValue = value.replace('-', ',');
            }
            params.append(paramKey, urlValue);
        });
    });
    if (options === null || options === void 0 ? void 0 : options.query) {
        params.set('q', options.query);
    }
    if (options === null || options === void 0 ? void 0 : options.sort) {
        params.set('sort', options.sort);
    }
    if ((options === null || options === void 0 ? void 0 : options.page) && options.page > 1) {
        params.set('page', String(options.page));
    }
    const qs = params.toString();
    window.history.pushState({}, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
}
function setFilterMetadataEntry(cache, filter) {
    var _a;
    const optionsMap = new Map();
    (_a = filter.options) === null || _a === void 0 ? void 0 : _a.forEach((option) => {
        optionsMap.set(option.id, option.label);
    });
    cache.set(filter.id, {
        label: filter.label,
        options: optionsMap,
        unit: filter.unit,
        min: filter.min,
        max: filter.max,
    });
}
/** Persist last non-empty facet list and refresh metadata cache for filter tags / drawer */
export function persistPreviousFiltersAndSyncMetadata(filters, previousFiltersRef, filterMetadataCache) {
    if (filters.length > 0) {
        previousFiltersRef.current = filters;
    }
    filters.forEach((filter) => setFilterMetadataEntry(filterMetadataCache.current, filter));
}
export function mergeFiltersWithActiveState(persistedFilters, activeFilters) {
    return persistedFilters.map((filter) => {
        const activeValues = activeFilters[filter.id];
        if (!activeValues || activeValues.length === 0) {
            return filter;
        }
        if (filter.type === 'range' || filter.type === 'slider') {
            const rangeValue = activeValues[0];
            if (rangeValue && rangeValue.includes('-')) {
                const [minStr, maxStr] = rangeValue.split('-');
                const currentMin = parseInt(minStr, 10);
                const currentMax = parseInt(maxStr, 10);
                if (!isNaN(currentMin) && !isNaN(currentMax)) {
                    return Object.assign(Object.assign({}, filter), { currentMin, currentMax });
                }
            }
        }
        if (filter.options) {
            return Object.assign(Object.assign({}, filter), { options: filter.options.map((option) => (Object.assign(Object.assign({}, option), { selected: activeValues.includes(option.id) }))) });
        }
        return filter;
    });
}
/**
 * Build ordered drawer items (standalone filters + facet groups). Mutates
 * `filterMetadataCache` for filters inside groups (for BirdsnestFilterTags labels).
 */
export function buildOrderedFilterItems(filtersWithActiveState, filterMetadataCache) {
    var _a, _b;
    const filtersByGroupLabel = new Map();
    const items = [];
    for (const filter of filtersWithActiveState) {
        const facetGroupValue = (_b = (_a = filter.customAttributes) === null || _a === void 0 ? void 0 : _a.find((attr) => attr.name === FILTER_GROUP_ATTRIBUTE_NAME)) === null || _b === void 0 ? void 0 : _b.value;
        if (facetGroupValue != null && facetGroupValue !== '') {
            let filtersInGroup = filtersByGroupLabel.get(facetGroupValue);
            if (!filtersInGroup) {
                filtersInGroup = [];
                filtersByGroupLabel.set(facetGroupValue, filtersInGroup);
            }
            const isFirstInGroup = filtersInGroup.length === 0;
            filtersInGroup.push(filter);
            if (isFirstInGroup) {
                items.push({
                    type: 'group',
                    group: {
                        groupId: `grouped_${facetGroupValue.toLowerCase().replace(/\s+/g, '_')}`,
                        groupLabel: facetGroupValue,
                        filters: filtersInGroup,
                    },
                });
            }
        }
        else {
            items.push({ type: 'filter', filter });
        }
    }
    items.forEach((item) => {
        if (item.type !== 'group')
            return;
        item.group.filters.forEach((f) => setFilterMetadataEntry(filterMetadataCache.current, f));
    });
    return items;
}
/**
 * Fredhopper search tabs (all / styles / outfits / …): merge URL/drawer facets with
 * `product_type` for the active tab.
 *
 * The `styles` tab is a meta-tab: it groups every product_type that *isn't* one of
 * the dedicated tabs (outfit/capsule/video/preloved). The caller passes the live list
 * of available product_types from the Fredhopper facet so we can compute the complement.
 */
export const STYLES_FILTER_TAB_EXCLUDED_PRODUCT_TYPES = new Set(['outfit', 'capsule', 'video', 'preloved']);
export function mergeSearchTabProductTypeFilters(currentTabId, facets, allProductTypes = []) {
    if (currentTabId === 'article')
        return facets;
    if (currentTabId === 'all')
        return Object.assign({}, facets);
    if (currentTabId === 'styles') {
        const stylesTypes = allProductTypes.filter(t => !STYLES_FILTER_TAB_EXCLUDED_PRODUCT_TYPES.has(t));
        return Object.assign(Object.assign({}, facets), { product_type: stylesTypes });
    }
    return Object.assign(Object.assign({}, facets), { product_type: [currentTabId] });
}
// On the search results page, `product_type` is driven by the result tabs
// (styles / outfits / videos / …), so surfacing it in the drawer too would let
// users pick a value that contradicts the active tab. It's hidden there but
// shown on regular collection PLPs. (Birdsnest TV renders no drawer at all, so
// it needs no handling here.)
const DRAWER_HIDDEN_FILTER_IDS = new Set(['product_type']);
/**
 * The search results page mounts `BirdsnestSearchPage` on `#search-app-page`;
 * collection / PLP / Style Matches mount `BirdsnestDemoPage` on `#search-app`.
 * The presence of `#search-app-page` is the structural signal for "search page".
 */
function isSearchPage() {
    return typeof document !== 'undefined' && document.getElementById('search-app-page') != null;
}
export function useFilterDrawerState(filters, activeFilters) {
    const previousFiltersRef = useRef([]);
    const filterMetadataCache = useRef(new Map());
    // Search page hides the tab-driven `product_type` facet; other pages show every facet.
    const visibleFilters = useMemo(() => isSearchPage() ? filters.filter(f => !DRAWER_HIDDEN_FILTER_IDS.has(f.id)) : filters, [filters, isSearchPage()]);
    useEffect(() => {
        persistPreviousFiltersAndSyncMetadata(visibleFilters, previousFiltersRef, filterMetadataCache);
    }, [visibleFilters]);
    const persistedFilters = visibleFilters.length > 0 ? visibleFilters : previousFiltersRef.current;
    const filtersWithActiveState = useMemo(() => mergeFiltersWithActiveState(persistedFilters, activeFilters), [persistedFilters, activeFilters]);
    const orderedFilterItems = useMemo(() => buildOrderedFilterItems(filtersWithActiveState, filterMetadataCache), [filtersWithActiveState]);
    return { filterMetadataCache, orderedFilterItems };
}
